/**
 * Animation: Duration tokens
 */
const animationDurations = {
  /**
   * Disabled for preferred reduced motion.
   * Making it almost zero to ensure that JavaScript animation callbacks
   * still work correctly.
   */
  durationDisabled: '0.01ms',
  /**
   * The default animation speed for most things.
   *
   * @see https://www.nngroup.com/articles/animation-duration/
   */
  duration: '100ms',
  durationFast: '80ms',
  durationFastest: '60ms',
  durationSlow: '160ms',
  durationSlower: '200ms',
  durationSlowest: '240ms',
  /**
   * Used only for debugging.
   *
   * It slows down animations to 5 seconds, allowing you to reaaaally
   * see things play out.
   */
  durationDebug: '5000ms',
} as const;

/**
 * Animation: Stagger tokens
 *
 * "Staggering" is a technique to sequentially play multiple animations
 * together with slightly different timings.
 *
 * Below is a rough illustration of 3 animation durations playing with
 * staggered timings:
 *
 * @example
 * ```
 * ===
 *   ===
 *     ===
 *       ===
 * ```
 *
 * With CSS animations, staggering is accomplished through these properties:
 *
 * - `animation-delay`
 * - `transition-delay`
 *
 * For our animations, we're going to be staggering in steps of 60ms.
 */
const animationStagger = {
  stagger0: '0ms',
  stagger1: '60ms',
  stagger2: '120ms',
  stagger3: '180ms',
  stagger4: '240ms',
  stagger5: '300ms',
  stagger6: '360ms',
  stagger7: '420ms',
  stagger8: '480ms',
} as const;

/**
 * Animation: Easing tokens
 *
 * The most commonly used ones will be linear, ease, and bounce.
 */
const animationEasings = {
  ease: 'ease',
  easeIn: 'ease-in',
  easeOut: 'ease-out',
  /**
   * A standard animation timing without any deviation in timing (or drama).
   *
   * Always use linear easing for opacity animations.
   */
  linear: 'linear',
  easeInOut: 'ease-in-out',
  /**
   * A slight overshoot.
   *
   * Custom cubic beziers are needed to replicate the (really nice)
   * physics based "spring" animations found in more immersive animation
   * experinces.
   *
   * This is useful for animations that involve an adjustment in sizing.
   *
   * @see https://react-spring.io/#why-springs-and-not-durations
   */
  bounce: 'cubic-bezier(0.680, -0.650, 0.265, 1.650)',
  /**
   * An exaggerated overshoot... but no too much.
   *
   * Custom cubic beziers are needed to replicate the (really nice)
   * physics based "spring" animations found in more immersive animation
   * experinces.
   *
   * This is useful for animations that involve an adjustment in sizing.
   *
   * @see https://react-spring.io/#why-springs-and-not-durations
   */
  elastic: 'cubic-bezier(0.680, -1.500, 0.265, 2.500)',
} as const;

/**
 * Animation n tokens
 */
export const animations = {
  ...animationDurations,
  ...animationEasings,
  ...animationStagger,
} as const;
